import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Banknote, Loader2, TrendingUp, Users, Percent, AlertCircle, CheckCircle2 } from "lucide-react";

const money = (n) => `$${(n ?? 0).toFixed(2)}`;

export default function Owner() {
  const [data, setData] = useState(null);
  const [connect, setConnect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [ov, st] = await Promise.all([api.get("/owner/overview"), api.get("/connect/status")]);
      setData(ov.data);
      setConnect(st.data);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onboard = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard");
      if (data.connect_available && data.url) {
        window.location.href = data.url;
      } else {
        toast.info(data.message || "Connect not available yet");
        setConnecting(false);
      }
    } catch (e) {
      toast.error("Could not start onboarding");
      setConnecting(false);
    }
  };

  const stats = [
    { icon: TrendingUp, label: "Gross Revenue", value: money(data?.gross_revenue), sub: "all subscriptions" },
    { icon: Percent, label: `Platform Fee (${data?.fee_percent ?? 2}%)`, value: money(data?.platform_fees), sub: "kept by platform" },
    { icon: Banknote, label: "Your Net Earnings", value: money(data?.net_earnings), sub: "to your bank", accent: true },
    { icon: Users, label: "Active Subscribers", value: data?.subscribers ?? 0, sub: "paying members" },
  ];

  return (
    <div className="bg-bg0 min-h-screen">
      <Navbar />
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-12">
        <div className="overline mb-3 text-volt">Owner Studio</div>
        <h1 className="font-display uppercase text-6xl md:text-7xl font-bold tracking-tighter mb-10">Revenue</h1>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[...Array(4)].map((_, i) => <div key={i} className="h-40 ep-loading" />)}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-5" data-testid="owner-stats">
              {stats.map((s, i) => (
                <div key={i} className={`ep-card p-6 ${s.accent ? "bg-volt text-black" : ""}`} data-testid={`owner-stat-${i}`}>
                  <s.icon size={22} strokeWidth={1.5} className={s.accent ? "text-black" : "text-volt"} />
                  <div className={`font-mono-accent text-xs uppercase tracking-[0.2em] font-bold mt-6 ${s.accent ? "text-black/70" : "text-epsub"}`}>{s.label}</div>
                  <div className="font-display text-4xl font-bold tracking-tighter mt-2">{s.value}</div>
                  <div className={`text-xs mt-1 ${s.accent ? "text-black/60" : "text-epmuted"}`}>{s.sub}</div>
                </div>
              ))}
            </div>

            <div className="ep-card p-8 mt-8" data-testid="connect-panel">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <h2 className="font-display uppercase text-2xl font-semibold tracking-tight">Payout Account</h2>
                  <p className="text-epsub mt-2 max-w-xl text-sm leading-relaxed">
                    Connect your bank via Stripe to collect subscription revenue. The platform automatically
                    keeps a {data?.fee_percent ?? 2}% fee on each payment; the rest lands in your account.
                  </p>
                  {connect?.connect_available === false && (
                    <div className="flex items-start gap-2 mt-4 text-sm text-yellow-400/90" data-testid="connect-unavailable">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{connect.message}</span>
                    </div>
                  )}
                  {connect?.connected && (
                    <div className="flex items-center gap-2 mt-4 text-sm" data-testid="connect-connected">
                      {connect.charges_enabled ? <CheckCircle2 size={16} className="text-volt" /> : <AlertCircle size={16} className="text-yellow-400" />}
                      <span className="text-white/90">
                        {connect.charges_enabled ? "Bank connected & active" : "Onboarding in progress — finish verification"}
                      </span>
                    </div>
                  )}
                </div>
                <button
                  onClick={onboard}
                  disabled={connecting || connect?.connect_available === false}
                  data-testid="connect-bank-btn"
                  className="ep-btn shrink-0 inline-flex items-center gap-2 bg-volt text-black px-6 py-3.5 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover disabled:opacity-50"
                >
                  {connecting ? <><Loader2 size={16} className="animate-spin" /> Opening…</> : <><Banknote size={16} /> {connect?.connected ? "Manage Payouts" : "Connect Bank"}</>}
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
